import React, { useState, useEffect, useCallback, useRef } from 'react';
import { whyNotLoaded } from '../utils/reportError';
import api from '../services/api';

// Finance · CFO Cockpit — one-screen summary composed from the finance modules.
const inr = (v) => (v < 0 ? '-₹' : '₹') + Math.abs(Math.round(Number(v) || 0)).toLocaleString('en-IN');
const input = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const curMonth = () => new Date().toISOString().slice(0, 7);

function Card({ title, value, sub, c = '#1a1a2e', bg = '#fff', accent }) {
  return (
    <div style={{ background: bg, border: '1px solid #E8E7E7', borderTop: '3px solid ' + (accent || c), borderRadius: 10, padding: '14px 16px', minWidth: 190, flex: '1 1 190px' }}>
      <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: c, marginTop: 3 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function CFOCockpit() {
  const [companies, setCompanies] = useState([]); const [canSwitch, setCanSwitch] = useState(false); const [company, setCompany] = useState('');
  const [fc, setFc] = useState(null); const [aging, setAging] = useState(null); const [margin, setMargin] = useState(null); const [db, setDb] = useState(null); const [pdc, setPdc] = useState(null);
  const H = useCallback(() => ({ headers: { 'X-Company-Id': company } }), [company]);
  // A FAILED SECTION SHOWS '—' AND SAYS WHY; IT NEVER SHOWS Rs 0 (3 Oct 2026). Cash, receivables,
  // payables, overdue and opening cash used to read `|| 0` over a failed read: a wrong number shown as
  // real. Each read now records its own failure, every section is cleared before a book's reads go
  // out, and an answer for a book no longer selected is dropped, so one book's figures never sit
  // under another's name.
  const [errs, setErrs] = useState({}); const [companyErr, setCompanyErr] = useState('');
  const loadSeq = useRef(0);

  useEffect(() => {
    api.get('/finance/daybook/companies').then(r => { setCompanies(r.data.data || []); setCanSwitch(r.data.can_switch); setCompany(r.data.active || (r.data.data[0] && r.data.data[0].id) || ''); })
      .catch(e => setCompanyErr(whyNotLoaded(e)));
  }, []);

  const load = useCallback(() => {
    if (!company) return;
    const seq = ++loadSeq.current;
    setFc(null); setAging(null); setMargin(null); setDb(null); setPdc(null); setErrs({});
    const read = (key, url, set) => api.get(url, H())
      .then(r => { if (seq === loadSeq.current) set(r); })
      .catch(e => { if (seq === loadSeq.current) setErrs(x => ({ ...x, [key]: whyNotLoaded(e) })); });
    read('forecast', '/finance/forecast', r => setFc(r.data.data));
    read('aging', '/finance/aging/summary', r => setAging(r.data.data));
    read('margin', '/finance/margin/orders', r => setMargin(r.data.totals));
    read('day book', '/finance/daybook/summary?month=' + curMonth(), r => setDb(r.data.data || r.data));
    read('PDC alerts', '/finance/pdc/alerts', r => setPdc(r.data.data));
  }, [company, H]);
  useEffect(() => { load(); }, [load]);

  const cash = (fc?.position?.cash_balance || 0) + (fc?.position?.bank_balance || 0);
  const recv = aging?.receivables?.total_outstanding || 0;
  const pay = aging?.payables?.total_outstanding || 0;
  const overdueRec = aging ? (aging.receivables.buckets['31_60'] + aging.receivables.buckets['61_90'] + aging.receivables.buckets['90_plus'] + aging.receivables.buckets['0_30']) : 0;
  const overduePay = aging ? (aging.payables.buckets['31_60'] + aging.payables.buckets['61_90'] + aging.payables.buckets['90_plus'] + aging.payables.buckets['0_30']) : 0;

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e' }}>CFO Cockpit</div>
          <div style={{ fontSize: 11, color: '#888' }}>Cash · margin · payables vs receivables · 90-day liquidity</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>Company</span>
          <select value={company} disabled={!canSwitch} onChange={e => setCompany(e.target.value)} style={{ ...input, fontWeight: 700, color: '#3B3A8F', background: canSwitch ? '#EEEDF8' : '#f5f5f5' }}>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {(companyErr || Object.keys(errs).length > 0) && (
        <div style={{ background: '#FCEBEB', color: '#A32D2D', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>
          {companyErr ? `Could not load the list of books (${companyErr}), so no figures were asked for.`
            : <>Not shown, because they could not be loaded for this book: {Object.entries(errs).map(([k, v]) => `${k} (${v})`).join(' · ')}. Cards marked — are missing, not zero.</>}
        </div>
      )}
      {fc?.liquidity_warning && (
        <div style={{ background: '#FDECEC', border: '1px solid #f5b5b5', color: '#A32D2D', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, fontWeight: 700 }}>
          ⚠️ Liquidity warning — projected cash goes negative within 90 days (90-day close {inr(fc.horizon.d90)}). See Cash-Flow Forecast.
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <Card title="Cash Position" value={fc ? inr(cash) : '—'} sub={fc?.position?.as_of_date ? 'as of ' + String(fc.position.as_of_date).slice(0, 10) : 'cash + bank'} accent="#3B3A8F" />
        <Card title="Receivables Outstanding" value={aging ? inr(recv) : '—'} sub={aging ? `overdue ${inr(overdueRec)}` : ''} c="#166534" accent="#166534" />
        <Card title="Payables Outstanding" value={aging ? inr(pay) : '—'} sub={aging ? `overdue ${inr(overduePay)}` : ''} c="#A32D2D" accent="#A32D2D" />
        <Card title="Net Working Position" value={aging ? inr(recv - pay) : '—'} sub="receivables − payables" c={recv - pay >= 0 ? '#166534' : '#A32D2D'} accent={recv - pay >= 0 ? '#166534' : '#A32D2D'} />
        <Card title="90-Day Liquidity" value={fc ? (fc.liquidity_warning ? 'At risk' : 'Healthy') : '—'} sub={fc ? '90-day close ' + inr(fc.horizon.d90) : ''} c={fc?.liquidity_warning ? '#A32D2D' : '#166534'} accent={fc?.liquidity_warning ? '#A32D2D' : '#166534'} />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <Card title="Order Margin (open)" value={margin ? inr(margin.act_margin) : '—'} sub={margin ? `est ${inr(margin.est_margin)} · ${margin.flagged} over-budget${margin.incomplete_actuals ? ` · ⚠ ${margin.incomplete_actuals} incomplete actuals` : ''}` : ''} c={margin && margin.act_margin < margin.est_margin ? '#A32D2D' : '#166534'} accent="#0E7490" />
        <Card title="Paid this Month (Day Book)" value={db ? inr(db.total_out ?? db.total_actual ?? ((db.cash || 0) + (db.bank || 0))) : '—'} sub={db && db.total_budget != null ? `budget ${inr(db.total_budget)}` : ''} accent="#854F0B" />
        <Card title="Opening Cash" value={fc ? inr(fc.opening || 0) : '—'} sub={fc ? `run-rate ${inr(fc.run_rate_monthly || 0)}/mo` : ''} accent="#3B3A8F" />
        <Card title="PDCs Clearing (15d)" value={pdc ? inr(pdc.upcoming_total) : '—'}
          sub={pdc ? `${pdc.upcoming_count} cheque(s)${pdc.overdue_count ? ` · +${pdc.overdue_count} overdue` : ''}` : ''}
          c={pdc && pdc.upcoming_count ? '#854F0B' : '#166534'} accent="#854F0B" />
      </div>

      {/* PDCs clearing within 15 days (CEO heads-up) */}
      {pdc && (pdc.upcoming_count > 0 || pdc.overdue_count > 0) && (
        <div style={{ border: '1px solid #F0E0BF', background: '#FFFDF7', borderRadius: 10, padding: 16, marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#854F0B' }}>🧾 Post-Dated Cheques clearing within 15 days</div>
            {pdc.overdue_count > 0 && (
              <span style={{ fontSize: 12, fontWeight: 700, color: '#A32D2D', background: '#FCEBEB', padding: '3px 10px', borderRadius: 10 }}>
                ⚠ {pdc.overdue_count} overdue · {inr(pdc.overdue_total)}
              </span>
            )}
          </div>
          {pdc.upcoming_count === 0 ? (
            <div style={{ fontSize: 12, color: '#888' }}>None clearing in the next 15 days.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ color: '#888', textAlign: 'left' }}>
                {['Cheque', 'Party', 'Bank', 'Amount', 'Due', 'In'].map(h => <th key={h} style={{ padding: '4px 8px', fontWeight: 600, borderBottom: '1px solid #eee' }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {pdc.upcoming.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace', fontWeight: 700 }}>{r.cheque_no}</td>
                    <td style={{ padding: '5px 8px' }}>{r.party_name}</td>
                    <td style={{ padding: '5px 8px', color: '#888' }}>{r.bank_account || '—'}</td>
                    <td style={{ padding: '5px 8px', fontWeight: 600 }}>{inr(r.amount)}</td>
                    <td style={{ padding: '5px 8px' }}>{String(r.due_date).slice(0, 10)}</td>
                    <td style={{ padding: '5px 8px', color: r.days_to_due <= 3 ? '#A32D2D' : '#854F0B', fontWeight: 700 }}>{r.days_to_due}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* 90-day projected balance trend */}
      <div style={{ border: '1px solid #E8E7E7', borderRadius: 10, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 12 }}>Projected balance (next 90 days)</div>
        {!fc ? <div style={{ fontSize: 12, color: errs.forecast ? '#A32D2D' : '#888' }}>{errs.forecast ? `Not shown: the forecast could not be loaded (${errs.forecast}).` : 'Loading…'}</div> :
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          {[['Opening', fc?.opening], ...(fc?.periods || []).map(p => [p.label, p.closing])].map(([l, v], i) => {
            const max = Math.max(1, ...(fc ? [Math.abs(fc.opening), ...fc.periods.map(p => Math.abs(p.closing))] : [1]));
            const h = Math.max(6, Math.round((Math.abs(v || 0) / max) * 110));
            const neg = (v || 0) < 0;
            return (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: neg ? '#A32D2D' : '#166534', marginBottom: 4 }}>{inr(v || 0)}</div>
                <div style={{ height: h, background: neg ? '#E9786F' : '#7FB089', borderRadius: 4 }} />
                <div style={{ fontSize: 10, color: '#888', marginTop: 5 }}>{l}</div>
              </div>
            );
          })}
        </div>}
      </div>
    </div>
  );
}
